"use client"

import { useState, useEffect, useRef } from "react"
import { useSearchParams } from "next/navigation"
import { Header } from "@/components/header"
import { Footer } from "@/components/footer"
import { PropertyFilters } from "@/components/property-filters"
import { VirtualizedPropertyGrid } from "@/components/virtualized-property-grid"
import { PropertyGridSkeleton } from "@/components/property-skeleton"
import { PropertyMap } from "@/components/property-map"
import { Button } from "@/components/ui/button"
import { useInfiniteProperties } from "@/hooks/useInfiniteProperties"
import { useAuth } from "@/contexts/AuthContext"
import { useCities } from "@/hooks/useCities"
import { Map, X, AlertCircle, RefreshCw, Plus } from "lucide-react"
import Link from "next/link"

interface FilterState {
  city: string
  priceSort: string
  searchText: string
}

export default function PropiedadesPage() {
  const searchParams = useSearchParams()
  const isInitialMount = useRef(true)
  const { isAuthenticated } = useAuth()
  const { cities, loading: citiesLoading } = useCities()

  // Initialize filters using v17 structure
  const [filters, setFilters] = useState<FilterState>(() => {
    const cityIdParam = searchParams.get("cityId")
    let cityName = ""

    // Convert cityId to city name for v17 compatibility
    if (cityIdParam) {
      const cityMap: Record<string, string> = {
        "1": "Valencia",
        "2": "Pamplona",
        "3": "Salamanca",
      }
      cityName = cityMap[cityIdParam] || ""
    }

    return {
      city: cityName,
      priceSort: "none",
      searchText: "",
    }
  })

  const [showMap, setShowMap] = useState(false)

  // Set default city to first city when cities are loaded
  useEffect(() => {
    if (cities.length > 0) {
      setFilters(prev => {
        if (!prev.city) return { ...prev, city: cities[0].name }
        return prev
      })
    }
  }, [cities])

  const {
    properties,
    loading,
    loadingMore,
    error,
    totalCount,
    hasNextPage,
    loadMore,
    refetch,
    clearError,
    isInitialLoad,
  } = useInfiniteProperties(filters, cities)

  // Handle URL params changes (for footer city links)
  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false
      return
    }

    const cityIdParam = searchParams.get("cityId")
    if (cityIdParam) {
      const cityMap: Record<string, string> = {
        "1": "Valencia",
        "2": "Pamplona",
        "3": "Salamanca",
      }
      const cityName = cityMap[cityIdParam]
      if (cityName) {
        setFilters((prev) => {
          if (cityName !== prev.city) return { ...prev, city: cityName }
          return prev
        })
      }
    } else if (cities.length > 0) {
      setFilters((prev) => {
        if (!prev.city) return { ...prev, city: cities[0].name }
        return prev
      })
    }
  }, [searchParams, cities])

  const handleFiltersChange = (newFilters: FilterState) => {
    // console.log("Filters changed:", newFilters)
    setFilters(newFilters)
  }

  // Error state
  if (error && !properties.length) {
    return (
      <div className="flex flex-col min-h-screen">
        <Header />
        <main className="flex-1 bg-gray-50">
          <div className="container mx-auto px-4 py-8">
            <div className="flex flex-col items-center justify-center py-12 space-y-4">
              <AlertCircle className="h-12 w-12 text-red-400" />
              <h2 className="text-2xl font-bold text-gray-800">Error al cargar propiedades</h2>
              <p className="text-lg text-red-600 text-center max-w-md">{error}</p>
              <Button
                onClick={() => {
                  clearError()
                  refetch()
                }}
                className="bg-raime-golden-brown hover:bg-raime-golden-brown/90 text-white"
              >
                <RefreshCw className="h-4 w-4 mr-2" />
                Reintentar
              </Button>
            </div>
          </div>
        </main>
        <Footer />
      </div>
    )
  }

  return (
    <div className="flex flex-col min-h-screen">
      <Header />
      <main className="flex-1 bg-gray-50 pt-20">
        <div className="container mx-auto px-4 py-8">
          {/* Header */}
          <div className="mb-8">
            <h1 className="text-3xl md:text-4xl font-bold text-raime-dark-blue mb-4">
              {loading && isInitialLoad ? "Cargando..." : `${totalCount.toLocaleString()} Alojamientos`}
            </h1>
            <p className="text-lg text-muted-foreground">
              Explora nuestras propiedades disponibles en las mejores ubicaciones de España
            </p>
          </div>

          {/* Filters */}
          <PropertyFilters filters={filters} onFiltersChange={handleFiltersChange} />

          {/* Results Header */}
          <div className="flex justify-between items-center mt-6 mb-4">
            <div className="text-sm text-muted-foreground">
              {loading && isInitialLoad ? (
                "Cargando propiedades..."
              ) : (
                <>
                  Mostrando {properties.length.toLocaleString()} de {totalCount.toLocaleString()} propiedades
                  {loadingMore && " • Cargando más..."}
                </>
              )}
            </div>
            <div className="flex items-center gap-2">
              {/* Create Property Button - Only visible for authenticated users */}
              {isAuthenticated && (
                <Link href="/admin/propiedades/crear">
                  <Button
                    variant="outline"
                    className="flex items-center gap-2 border-raime-golden-brown text-raime-golden-brown hover:bg-raime-golden-brown hover:text-white bg-transparent"
                  >
                    <Plus className="h-4 w-4" />
                    Crear
                  </Button>
                </Link>
              )}
              <Button
                onClick={() => setShowMap(!showMap)}
                variant="outline"
                className="flex items-center gap-2 border-raime-golden-brown text-raime-golden-brown hover:bg-raime-golden-brown hover:text-white"
              >
                {showMap ? <X className="h-4 w-4" /> : <Map className="h-4 w-4" />}
                {showMap ? "Ocultar Mapa" : "Ver Mapa"}
              </Button>
            </div>
          </div>

          {/* Main Content */}
          <div className={`grid gap-8 ${showMap ? "lg:grid-cols-2" : ""}`}>
            {/* Properties Grid */}
            <div className="space-y-6">
              {/* Initial Loading State */}
              {loading && isInitialLoad ? (
                <PropertyGridSkeleton count={8} compact={showMap} />
              ) : properties.length === 0 ? (
                /* No Results */
                <div className="text-center py-12">
                  <div className="max-w-md mx-auto">
                    <div className="text-6xl mb-4">🏠</div>
                    <h3 className="text-xl font-semibold text-gray-800 mb-2">No se encontraron propiedades</h3>
                    <p className="text-gray-600 mb-4">
                      No hay propiedades que coincidan con los filtros seleccionados.
                    </p>
                    <Button
                      onClick={() => {
                        setFilters({
                          city: cities.length > 0 ? cities[0].name : "",
                          priceSort: "none",
                          searchText: "",
                        })
                      }}
                      variant="outline"
                      className="border-raime-golden-brown text-raime-golden-brown hover:bg-raime-golden-brown hover:text-white"
                    >
                      Limpiar Filtros
                    </Button>
                  </div>
                </div>
              ) : (
                /* Properties Grid with Infinite Scroll */
                <VirtualizedPropertyGrid
                  properties={properties}
                  loading={loading}
                  loadingMore={loadingMore}
                  hasNextPage={hasNextPage}
                  onLoadMore={loadMore}
                  showMap={showMap}
                />
              )}
            </div>

            {/* Map - Only show when showMap is true */}
            {showMap && (
              <div className="lg:sticky lg:top-8 h-[600px]">
                <PropertyMap properties={properties} />
              </div>
            )}
          </div>
        </div>
      </main>
      <Footer />
    </div>
  )
}
